<template>
  <nav v-if="$store.display.breakpoint.mdAndUp" class="bg-white border-b border-gray-200 z-50">
    <div class="n-max-width flex h-full items-end space-x-8">
      <!-- logo -->
      <NuxtLink to="/" class="my-auto">
        <img class="block h-8 py-1 w-auto" src="~assets/images/logo-edvenn.svg" alt="Edvenn logo" />
      </NuxtLink>

      <!-- Home -->
      <NuxtLink v-if="$rights.isTrainee" to="/" custom v-slot="{ href, navigate, isActive }">
        <a :href="href" @click="navigate"
          :class="[isActive ? 'border-blue text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700']"
          class="inline-flex items-center px-1 pt-1 pb-2 border-b-2 text-sm font-medium">Accueil</a>
      </NuxtLink>

      <!-- items -->
      <template v-for="item in items">
        <Menu as="div"
          :class="[item.href === $route.fullPath || $route.fullPath.startsWith(item.href + '/') ? 'border-blue text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700']"
          class="inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium">
          <div class="relative z-50 h-full w-full">
            <MenuButton class="pb-2">
              <NuxtLink v-if="!item.items?.length" :to="item.href">
                <span>{{ item.name }}</span>
                <span v-if="item.warning" class="menu-item-warning"></span>
              </NuxtLink>
              <template v-else>
                <span>{{ item.name }}</span>
                <span v-if="item.warning" class="menu-item-warning"></span>
              </template>
            </MenuButton>
            <transition enter-active-class="transition ease-out duration-200"
              enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95">
              <MenuItems style="width: 300%;"
                class="origin-top-right absolute mt-2 rounded-md shadow-lg py-1 bg-white focus:outline-none">
                <MenuItem as="span" v-for="itemC in item.items"
                  :key="typeof itemC.href === 'string' ? itemC.href : itemC.href.name" v-slot="{ active, close }">
                <NuxtLink as="a" :to="itemC.href" @click="close" active-class="font-bold"
                  :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700']">
                  <span>{{ itemC.name }}</span>
                  <span v-if="itemC.warning" class="menu-item-warning"></span>
                </NuxtLink>
                </MenuItem>
              </MenuItems>
            </transition>
          </div>
        </Menu>
      </template>

      <div class="flex-grow"></div>

      <!-- Account -->
      <div class="ml-6 h-full flex items-center">

        <!-- contact search -->
        <button @click="$rootEmitter.modalContactSearch()"
          v-if="$rights.isUser && $route.name !== 'admin-contact-search'">
          <span class="sr-only">Search contact</span>
          <MagnifyingGlassIcon class="w-5" />
        </button>

        <!-- waiting attendance -->
        <NuxtLink v-if="cpIdWaitingAttendance" custom v-slot="{ href, navigate }"
          :to="{ name: 'training-id-attendance', params: { id: cpIdWaitingAttendance } }">
          <a :href="href" @click="navigate" title="émargement en attente"
            class="inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700">
            <AcademicCapIcon class="h-5 w-5 text-orange-600" />
          </a>
        </NuxtLink>

        <!-- Profile dropdown -->
        <Menu as="div" class="ml-3 relative">
          <MenuButton
            class="bg-white text-gray-400 flex items-center text-sm rounded-full focus:outline-none hover:ring-1 hover:ring-offset-1 hover:ring-blue focus:ring-1 focus:ring-offset-1 focus:ring-blue">
            <span class="sr-only">Open user menu</span>
            <UserIcon class="!cursor-pointer" :size="1.5"
              :account="$rights.isUser ? $store.account.$state.account : undefined" />
          </MenuButton>
          <transition enter-active-class="transition ease-out duration-200"
            enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95">
            <MenuItems
              class="z-40 origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
              <MenuItem v-for="item in myAccount" :key="item.name" v-slot="{ active }">
              <div>
                <NuxtLink v-if="item.href" :to="item.href"
                  class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                  {{ item.name }}
                </NuxtLink>
                <a v-else-if="item.click" @click="item.click" target="_blank"
                  class="block cursor-pointer px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                  {{ item.name }}
                </a>
                <span v-else :class="[item.class || '', 'block px-4 py-2 text-sm text-gray-700']">
                  {{ item.name }}
                </span>
              </div>
              </MenuItem>
            </MenuItems>
          </transition>
        </Menu>
      </div>
    </div>
  </nav>
  <!-- Mobile - menu button -->
  <Disclosure v-else as="nav" class="bg-white border-b border-gray-200 z-50" v-slot="{ open }">
    <div class="n-max-width h-full flex items-center">
      <!-- logo -->
      <NuxtLink to="/">
        <img class="block h-7 py-1 w-auto" src="~assets/images/mini-logo-edvenn.svg" alt="Edvenn logo" />
      </NuxtLink>

      <div class="flex-grow"></div>

      <!-- waitingAttendance -->
      <NuxtLink v-if="cpIdWaitingAttendance"
        :to="{ name: 'training-id-attendance', params: { id: cpIdWaitingAttendance } }" custom
        v-slot="{ href, navigate }">
        <a :href="href" @click="navigate" title="émargement en attente"
          class="inline-flex items-center px-1 border-b-2 text-sm font-medium border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700">
          <AcademicCapIcon class="h-5 w-5 text-orange-600" />
        </a>
      </NuxtLink>

      <!-- contact search -->
      <t-button @click="$rootEmitter.modalContactSearch()" icon-only color="white" fill="clear" size="sm" class="h-3"
        v-if="$rights.isUser && $route.name !== 'admin-contact-search'">
        <span class="sr-only">Search contact</span>
        <MagnifyingGlassIcon class="w-5" />
      </t-button>

      <!-- mobile menu button -->
      <DisclosureButton
        class="bg-white inline-flex items-center justify-center p-1 m-1 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue">
        <span class="sr-only">Open main menu</span>
        <Bars3Icon v-if="!open" class="block h-6 w-6" aria-hidden="true" />
        <XMarkIcon v-else class="block h-6 w-6" aria-hidden="true" />
      </DisclosureButton>
    </div>

    <DisclosurePanel v-if="$store.display.breakpoint.sm" class="absolute bg-white z-50 w-full">
      <div class="pt-2 pb-3 space-y-1">
        <template v-for="item in items">
          <NuxtLink v-if="!item.items?.length" :key="item.name" :to="item.href" custom
            v-slot="{ href, navigate, isExactActive }">
            <DisclosureButton as="a" :href="href" class="border-b block pl-3 pr-4 py-2 border-l-4 text-base font-medium"
              :class="[isExactActive ? 'bg-blue-50 border-blue text-blue-700' : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800']"
              :aria-current="isExactActive ? 'page' : undefined" @click="navigate">
              <span class="pr-2">{{ item.name }}</span>
              <span v-if="item.warning" class="h-2 w-2 inline-block align-middle rounded-full bg-orange-600"></span>
            </DisclosureButton>
          </NuxtLink>
          <div v-else class="pl-4 border-b">{{ item.name }}</div>
          <NuxtLink v-for="itemC in item.items" :key="itemC.name" :to="itemC.href" custom
            v-slot="{ href, navigate, isActive }">
            <DisclosureButton as="a" :href="href"
              :class="[isActive ? 'bg-blue-50 border-blue text-blue-700' : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800', 'block pl-8 py-1 border-l-4 text-base font-medium']"
              :aria-current="isActive ? 'page' : undefined" @click="navigate">
              <span>{{ itemC.name }}</span>
              <span v-if="itemC.warning"
                class="ml-1 h-2 w-2 inline-block align-middle rounded-full bg-orange-600"></span>
            </DisclosureButton>
          </NuxtLink>
        </template>
      </div>

      <!-- Mobile - Account -->
      <div class="pt-4 pb-3 border-t border-gray-200">
        <div class="flex items-center px-4">
          <div class="flex-shrink-0">
            <UserIcon />
          </div>
          <div class="ml-3">
            <div class="text-base font-medium text-gray-800">
              {{ $store.account.account.firstname }} {{ $store.account.account.lastname }}
            </div>
            <div class="text-sm font-medium text-gray-500">
              {{ $store.account.account.email }}
            </div>
          </div>
        </div>
        <div class="mt-3 space-y-1">
          <template v-for="item in myAccount" :key="item.name">
            <NuxtLink v-if="item.href" :to="item.href" custom v-slot="{ href, navigate, isExactActive }">
              <DisclosureButton as="a" :href="href"
                :class="[isExactActive ? 'bg-blue-50 border-blue text-blue-700' : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800', 'block pl-3 pr-4 py-2 border-l-4 text-base font-medium']"
                :aria-current="isExactActive ? 'page' : undefined" @click="navigate">
                {{ item.name }}
              </DisclosureButton>
            </NuxtLink>
            <DisclosureButton as="a" v-else-if="item.click" @click="item.click" target="_blank"
              class="cursor-pointer border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800 block pl-3 pr-4 py-2 border-l-4 text-base font-medium">
              {{ item.name }}
            </DisclosureButton>
            <span v-else :class="item.class">
              {{ item.name }}
            </span>
          </template>
        </div>
      </div>
    </DisclosurePanel>
  </Disclosure>
</template>


<script lang="ts">
import { Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { Bars3Icon, XMarkIcon, MagnifyingGlassIcon } from '@heroicons/vue/24/outline'
import { AcademicCapIcon } from '@heroicons/vue/24/solid'
import type { RoutesNamedLocationsResolved } from '@typed-router/__routes'

import { mailtoSupport } from '@/util'
import { adminMenuRoutes, RouteAdmin } from '@/middleware/public.global'

export default defineComponent({
  components: {
    Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems,
    Bars3Icon, XMarkIcon, AcademicCapIcon, MagnifyingGlassIcon
  },

  setup() {
    const { $store, $router } = useNuxtApp()

    const cpIdWaitingAttendance = computed(() =>
      $store.account.rights.isUser ?
        undefined
        :
        $store.trainee.contactPackagesAll.find(cp => cp.waitingAttendance)?.contactPackageId
    )
    return {
      myAccount: computed(() => [
        { name: `${$store.account.account?.firstname} ${$store.account.account?.lastname}`, class: "border-b-2 hidden sm:block" },
        { name: 'Mon compte', href: '/account' },
        { name: 'Signaler un problème', click: () => window.open(mailtoSupport($store.account?.account?.email), '_blank') },
        { name: 'Déconnexion', click: () => $store.account.logout() }
      ]),

      cpIdWaitingAttendance,

      items: computed(() => {

        if ($store.account.rights.isUser)
          return adminMenuRoutes(...($store.account.token?.accessDecoded?.rights || [])) as RouteAdmin[]

        const contactPackages = $store.trainee.contactPackagesAll
        if (!contactPackages.length) return []

        const currentRouteName = ((_r) => {
          switch (_r) {
            case 'training-id-attendance':
            case 'training-id-calendar':
            case 'training-id-contract':
              return _r
            default:
              return 'training-id'
          }
        })($router.currentRoute.value.name.toString())

        return [{
          name: "Mes formations",
          href: "/training",
          warning: !!cpIdWaitingAttendance.value,
          items: contactPackages.map(cp => ({
            warning: cp.waitingAttendance,
            name: $store.cache.getPackage(cp.packageId)?.name,
            href: {
              name: currentRouteName,
              params: { id: cp.contactPackageId?.toString() }
            } satisfies RoutesNamedLocationsResolved
          }))
        }] as RouteAdmin[]
      })
    }
  }
})
</script>

<style lang="postcss">
.menu-item-warning {
  @apply ml-1 h-2 w-2 inline-block align-middle rounded-full bg-orange-600;
}
</style>